import React, {useMemo} from 'react';
import {Box, GridItem, useDisclosure} from '@chakra-ui/react';
import {State} from '@synako/halitrek';
import {useTranslation} from 'react-i18next';
import {LigneDePanierDesc} from 'api-types/ffconnect-hal';
import {LigneProblem, TriSelection} from 'api-types/ffconnect';
import {ProduitPanierAffiche} from './ProduitPanier/ProduitPanierAffiche';
import {ErreursLignesBlock} from './ErreursLignesBlock';
import {ProduitPanierError} from './ProduitPanier/ProduitPanierError';
import {SectionHeader} from '@/domains/ligneProduit/SectionHeader';
import {
  analyseLignes,
  groupLignesParCategorie,
  sortLignesDePanierParDate,
} from 'ff-common';
import {MatchingPdf} from '@/hooks/panier/usePanierPdf';
import {
  FiltreMatching,
  composerElementsPanierPdf,
  correspondAuFiltre,
} from './panier-pdf-lignes';
import {LigneMatchingOrpheline} from './LigneMatchingOrpheline';

export type LigneAvecProbleme = ReturnType<
  typeof analyseLignes
>['lignesAvecProbleme'][number];

export const PanierContent: React.FC<{
  date_de_livraison: string;
  lignesDePanier: State<LigneDePanierDesc>[];
  problemesLignes: LigneProblem[];
  validationRefresh: boolean;
  isMarketplace: boolean;
  triSelection: TriSelection;
  isPanierPdf: boolean;
  ordreLignesPdf: ReadonlyMap<string, number>;
  matchingPdf?: MatchingPdf;
  filtreMatching?: FiltreMatching;
}> = ({
  date_de_livraison,
  lignesDePanier,
  problemesLignes,
  validationRefresh,
  isMarketplace,
  triSelection,
  isPanierPdf,
  ordreLignesPdf,
  matchingPdf,
  filtreMatching,
}) => {
  const {t} = useTranslation();

  // Tri côté client pour afficher les plus récentes en premier (UX)
  // tout en gardant l'ordre ASC côté serveur (stabilité cache)
  const lignesTriees = useMemo(() => {
    const triParDate = sortLignesDePanierParDate(lignesDePanier);
    if (!isPanierPdf) return triParDate;
    // Panier PDF : on rejoue le tri dans l'ordre du bon de commande. Les lignes
    // sans correspondance (ajoutées après l'import) n'ont pas de rang et
    // finissent en queue, dans l'ordre par date.
    return triParDate.sort(
      (a, b) =>
        (ordreLignesPdf.get(a.data.id) ?? Infinity) -
        (ordreLignesPdf.get(b.data.id) ?? Infinity),
    );
  }, [lignesDePanier, ordreLignesPdf, isPanierPdf]);

  const {lignesAvecProbleme, problemes} = useMemo(
    () => analyseLignes(lignesTriees, problemesLignes),
    [lignesTriees, problemesLignes],
  );

  // Panier PDF : la liste affichée n'est plus celle des lignes de panier, mais
  // son union avec les lignes du bon de commande restées sans ligne de panier.
  // `undefined` partout ailleurs — y compris sur un panier PDF sans matching
  // exploitable, qui retombe alors sur le rendu à plat ordinaire.
  const elementsPdf = useMemo(() => {
    return matchingPdf
      ? composerElementsPanierPdf(lignesAvecProbleme, matchingPdf)
      : undefined;
  }, [lignesAvecProbleme, matchingPdf]);

  // Elements de l'import PDF à retourner selon le filtre appliqué.
  const elementsAffiches = useMemo(() => {
    return (
      elementsPdf?.filter((element) =>
        correspondAuFiltre(element, filtreMatching ?? 'tout'),
      ) ?? []
    );
  }, [elementsPdf, filtreMatching]);

  // Groupement des lignes par catégorie (tri_selection garanti par le backend)
  // Le panier PDF s'affiche à plat : pas de groupement à calculer.
  const categories = useMemo(() => {
    return isPanierPdf
      ? []
      : groupLignesParCategorie(
          lignesAvecProbleme,
          triSelection,
          (item) => item.ligneDePanier.data.code_article,
        );
  }, [lignesAvecProbleme, triSelection, isPanierPdf]);

  return (
    <GridItem colSpan={{base: 1, lg: 6}}>
      {problemes.length > 0 && (
        <Box
          w='100%'
          mb='4'
        >
          <ErreursLignesBlock
            nbErreurs={problemes.length}
            dateDeLivraison={date_de_livraison}
            isMarketplace={isMarketplace}
            t={t}
          >
            {problemes.map(({probleme, ligneDePanier}) => {
              const idError = `${probleme.problem.code}-${probleme.ligne_de_panier_id}`;
              return (
                <ProduitPanierError
                  key={idError}
                  idError={idError}
                  ligneDePanier={ligneDePanier}
                  probleme={probleme}
                  validationRefresh={validationRefresh}
                />
              );
            })}
          </ErreursLignesBlock>
        </Box>
      )}

      <Box
        width='100%'
        border='1px solid'
        borderColor='gray.light'
        borderRadius='12px'
        boxShadow='md'
        bg='white'
        overflow='hidden'
      >
        {/* Panier PDF : rendu à plat de la liste composée, où s'intercalent les
        lignes du bon de commande qui n'ont pas de ligne de panier */}
        {elementsPdf ? (
          elementsAffiches.length === 0 ? (
            // Au cas où on arrive sur un filtre avec 0 résultats.
            <Box
              px='12px'
              py='6'
              color='gray.dark'
              fontSize='sm'
            >
              {t('panier.matching_aucune_ligne_filtre')}
            </Box>
          ) : (
            <Box px='12px'>
              {elementsAffiches.map((element) => {
                if (element.type === 'orphelin') {
                  return (
                    <LigneMatchingOrpheline
                      key={element.ligneMatching.id}
                      ligneMatching={element.ligneMatching}
                      raison={element.raison}
                    />
                  );
                }
                const {ligneDePanier, probleme, matching} = element;
                const idError = probleme
                  ? `${probleme.problem.code}-${probleme.ligne_de_panier_id}`
                  : ligneDePanier.data.id;
                return (
                  <ProduitPanierAffiche
                    key={ligneDePanier.data.id}
                    idError={idError}
                    ligneDePanier={ligneDePanier}
                    probleme={probleme}
                    matching={matching}
                    afficherRemplacer
                  />
                );
              })}
            </Box>
          )
        ) : isPanierPdf || isMarketplace || categories.length === 0 ? (
          <Box px='12px'>
            {lignesAvecProbleme.map(({ligneDePanier, probleme}) => {
              const idError = probleme
                ? `${probleme.problem.code}-${probleme.ligne_de_panier_id}`
                : ligneDePanier.data.id;
              return (
                <ProduitPanierAffiche
                  key={ligneDePanier.data.id}
                  idError={idError}
                  ligneDePanier={ligneDePanier}
                  probleme={probleme}
                />
              );
            })}
          </Box>
        ) : (
          categories.map(({categorie_id, libelle_web, lignes}) => (
            <PanierCategorieSection
              key={categorie_id}
              id={`panier_cat_${categorie_id}`}
              title={libelle_web}
              lignes={lignes}
            />
          ))
        )}
      </Box>
    </GridItem>
  );
};

// Section catégorie du panier avec header collapsible
const PanierCategorieSection: React.FC<{
  id: string;
  title: string;
  lignes: LigneAvecProbleme[];
}> = React.memo(({id, title, lignes}) => {
  const {isOpen, onToggle} = useDisclosure();
  return (
    <SectionHeader
      title={title}
      count={lignes.length}
      id={id}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      {/* px pour inset les séparations inter-lignes (bordures plus courtes que la largeur du container) */}
      <Box px='12px'>
        {lignes.map(({ligneDePanier, probleme}) => {
          const idError = probleme
            ? `${probleme.problem.code}-${probleme.ligne_de_panier_id}`
            : ligneDePanier.data.id;
          return (
            <ProduitPanierAffiche
              key={ligneDePanier.data.id}
              idError={idError}
              ligneDePanier={ligneDePanier}
              probleme={probleme}
            />
          );
        })}
      </Box>
    </SectionHeader>
  );
});
